import api from '../api/axios';
import { getResultLookupType } from '../utils/resultOptions';
import {
  deleteDraft,
  getDraft,
  listDrafts,
  mutateDraft,
  readAttachmentBlob,
} from './drafts';
import { idbGet, idbPut, requestPersistentStorage, STORES } from './idb';
import { updateCounterFromServer } from './inspectionCode';
import { ensureServerReachable, isOffline } from './network';
import { getCachedUser, selectedAuctionOf } from './session';

const PREFETCH_KEY = 'ref-prefetched-at';
const PREFETCH_MIN_INTERVAL_MS = 10 * 60 * 1000;
const SYNCED_KEY_PREFIX = 'synced:';

let prefetchRunning = null;
let syncRunning = null;
const listeners = new Set();
let progress = { running: false, current: null, done: 0, total: 0 };

export function subscribeSync(listener) {
  listeners.add(listener);
  listener(progress);
  return () => listeners.delete(listener);
}

function setProgress(next) {
  progress = { ...progress, ...next };
  listeners.forEach((listener) => listener(progress));
}

function errorMessage(error) {
  return error?.userMessage || error?.response?.data?.message || error?.message || 'تعذّر الرفع.';
}

// ===== تنزيل البيانات المرجعية =====

// يُنزّل كل ما تحتاجه شاشات الفحص دون اتصال. يُستدعى عند الدخول وعند فتح التطبيق مع الاتصال.
export function prefetchReferenceData({ force = false } = {}) {
  if (prefetchRunning) return prefetchRunning;

  prefetchRunning = (async () => {
    if (isOffline()) return false;
    const last = Number((await idbGet(STORES.kv, PREFETCH_KEY).catch(() => 0)) || 0);
    if (!force && Date.now() - last < PREFETCH_MIN_INTERVAL_MS) return false;

    requestPersistentStorage();

    // كل طلب ناجح يُحفظ على الجهاز تلقائيًا (api/axios.js).
    const [typesRes] = await Promise.all([
      api.get('/inspection-types'),
      api.get('/manufacturers'),
      api.get('/models'),
      api.get('/lookups'),
      api.get('/categories'),
      api.get('/settings/company').catch(() => null),
      api.get(`/lookups/by-type/${getResultLookupType('generic')}`).catch(() => null),
      api.get(`/lookups/by-type/${getResultLookupType('body')}`).catch(() => null),
    ]);

    // الشاشات تجلب البنود بمفتاح نوع الفحص (SectionInspection وBodyInspection)، فنُنزّل بالمفتاح نفسه.
    const sectionKeys = new Set(['body', 'steering', 'engine']);
    (typesRes.data || []).forEach((type) => {
      if (type?.key) sectionKeys.add(type.key);
    });
    await Promise.all([...sectionKeys].map((key) => (
      api.get(`/inspection-items/by-section/${key}`).catch(() => null)
    )));

    await idbPut(STORES.kv, Date.now(), PREFETCH_KEY);
    return true;
  })().catch(() => false).finally(() => {
    prefetchRunning = null;
  });

  return prefetchRunning;
}

export async function refreshCodeCounter() {
  if (isOffline()) return null;
  try {
    const { data } = await api.get('/inspections/code-counter');
    await updateCounterFromServer(data);
    return data;
  } catch {
    return null;
  }
}

export async function lastPrefetchAt() {
  return Number((await idbGet(STORES.kv, PREFETCH_KEY).catch(() => 0)) || 0) || null;
}

// ===== المزامنة =====

// لا يُرفع فحص إلا بحساب صاحبه وعلى مزاده: الخادم يربط الفحص بالمستخدم والمزاد الحاليين.
export function syncBlocker(draft, user = getCachedUser()) {
  if (!user) return 'سجّل الدخول أولًا.';
  if (Number(user.id) !== Number(draft.user_id)) return 'هذا الفحص لمستخدم آخر. سجّل الدخول بحسابه لرفعه.';
  const auction = selectedAuctionOf(user);
  if (Number(auction?.id) !== Number(draft.auction_id)) return `هذا الفحص لمزاد آخر (رقم ${draft.auction_id}). سجّل الدخول على مزاده لرفعه.`;
  return null;
}

async function createOnServer(draft) {
  // لا نرسل auction_id: syncBlocker يضمن أن مزاد الفحص هو مزاد الدخول الحالي، والخادم يرفض
  // auction_id الصريح لمستخدم مرتبط بمزاده عبر branch_id فقط.
  const { data } = await api.post('/inspections', {
    ...draft.state.form,
    inspection_code: draft.code,
    client_uuid: draft.client_uuid,
  });

  await mutateDraft(draft.id, (current) => {
    current.sync.server_id = data.id;
    current.sync.server_code = data.inspection_code || null;
    current.sync.code_changed = Boolean(data.inspection_code && data.inspection_code !== current.code);
  });
  return data;
}

async function sendOp(draft, op) {
  const serverId = draft.sync.server_id;

  switch (op.type) {
    case 'update':
      await api.put(`/inspections/${serverId}`, draft.state.form);
      return null;
    case 'section':
      await api.post(`/inspections/${serverId}/${op.endpoint}`, { items: op.items });
      return null;
    case 'body':
      await api.post(`/inspections/${serverId}/body`, { items: op.items, mark_done: op.mark_done });
      return null;
    case 'computer':
      await api.post(`/inspections/${serverId}/computer`, op.payload);
      return null;
    case 'attachment': {
      const attachment = draft.state.attachments.find((entry) => entry.id === op.attachment_id);
      if (!attachment) return null;
      const blob = await readAttachmentBlob(attachment.blob_key);
      if (!blob) return null;
      const form = new FormData();
      form.append('file', blob, attachment.file_name || 'image.jpg');
      Object.entries(op.fields || {}).forEach(([key, value]) => form.append(key, value));
      const { data } = await api.post(`/inspections/${serverId}/attachments`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      return { attachmentId: attachment.id, serverAttachmentId: data?.id || null };
    }
    case 'delete-attachment':
      try {
        await api.delete(`/inspections/${serverId}/attachments/${op.server_id}`);
      } catch (error) {
        // حذفها الخادم مسبقًا (استُبدلت بصورة أحدث للبند نفسه).
        if (error?.response?.status !== 404) throw error;
      }
      return null;
    case 'complete':
      await api.patch(`/inspections/${serverId}/complete`, {});
      return null;
    default:
      return null;
  }
}

export async function syncDraft(draftId) {
  let draft = await getDraft(draftId);
  if (!draft) return { ok: true, skipped: true };

  if (!(await ensureServerReachable())) {
    return { ok: false, error: 'لا يوجد اتصال بالخادم الآن. أعد المحاولة عند توفر الاتصال.' };
  }

  const blocker = syncBlocker(draft);
  if (blocker) {
    await mutateDraft(draftId, (current) => {
      current.sync.last_error = blocker;
    });
    return { ok: false, error: blocker };
  }

  try {
    await mutateDraft(draftId, (current) => {
      current.sync.last_attempt_at = new Date().toISOString();
      current.sync.last_error = null;
    });

    if (!draft.sync.server_id) {
      await createOnServer(draft);
    }

    // كل عملية تُحذف من القائمة بعد نجاحها، فإن انقطعت الشبكة أكملنا من حيث توقفنا.
    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      draft = await getDraft(draftId);
      const op = draft.ops[0];
      if (!op) break;

      // eslint-disable-next-line no-await-in-loop
      const result = await sendOp(draft, op);
      // eslint-disable-next-line no-await-in-loop
      await mutateDraft(draftId, (current) => {
        current.ops = current.ops.filter((entry) => entry.id !== op.id);
        if (result?.attachmentId) {
          const attachment = current.state.attachments.find((entry) => entry.id === result.attachmentId);
          if (attachment) attachment.server_id = result.serverAttachmentId;
        }
      });
    }

    draft = await getDraft(draftId);
    const summary = {
      ok: true,
      server_id: draft.sync.server_id,
      code: draft.sync.server_code || draft.code,
      requested_code: draft.code,
      code_changed: Boolean(draft.sync.code_changed),
    };

    // رُفع بالكامل: يُحذف من الجهاز، ونحتفظ بأثر صغير لمن يفتح رابطه المحلي القديم.
    await idbPut(STORES.kv, { ...summary, synced_at: new Date().toISOString() }, `${SYNCED_KEY_PREFIX}${draftId}`);
    await deleteDraft(draftId);
    return summary;
  } catch (error) {
    const message = errorMessage(error);
    await mutateDraft(draftId, (current) => {
      current.sync.last_error = message;
    }).catch(() => {});
    return { ok: false, error: message, status: error?.response?.status };
  }
}

// onlyCompleted: الرفع التلقائي عند عودة الاتصال يرفع المكتمل فقط؛
// الفحص الذي لم يُنهَ يبقى على الجهاز حتى يكمله الموظف أو يرفعه يدويًا.
export function syncAll({ onlyCompleted = true } = {}) {
  if (syncRunning) return syncRunning;

  syncRunning = (async () => {
    if (!(await ensureServerReachable())) return [];
    const user = getCachedUser();
    const drafts = (await listDrafts()).filter((draft) => (
      (!onlyCompleted || draft.state?.status === 'completed') && !syncBlocker(draft, user)
    ));

    setProgress({ running: true, done: 0, total: drafts.length, current: null });
    const results = [];
    for (const draft of drafts) {
      setProgress({ current: draft.id });
      // eslint-disable-next-line no-await-in-loop
      const result = await syncDraft(draft.id);
      results.push({ id: draft.id, ...result });
      setProgress({ done: results.length });
      // انتهت صلاحية الدخول: لا فائدة من بقية الفحوصات الآن.
      if (result.status === 401) break;
    }
    if (results.some((result) => result.ok)) refreshCodeCounter();
    return results;
  })().finally(() => {
    syncRunning = null;
    setProgress({ running: false, current: null });
  });

  return syncRunning;
}

export async function syncedRecord(localId) {
  return idbGet(STORES.kv, `${SYNCED_KEY_PREFIX}${localId}`);
}
